import Image from "next/image";
import Link from "next/link";
import RightArrow from "@/public/icons/hiw-right-arrow.svg";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import ShareButton from "@/components/blog/shareButton/ShareButton";
import { Article } from "@/services/cms/apiTypes";
import { ReactMarkdown } from "react-markdown/lib/react-markdown";
import NextBlogPost from "@/components/blog/nextBlogPost/NextBlogPost";
import Screen from "@/constants/screens";

function formatISODateToReadableDate(inputDateString: string) {
  const date = new Date(inputDateString);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const year = date.getFullYear();
  const monthIndex = date.getMonth();
  const day = date.getDate();

  const formattedDateString = `${monthNames[monthIndex]} ${day}, ${year}`;

  return formattedDateString;
}

interface BlogProps {
  article: Article;
  baseUrl: string;
}
export const ArticleContent = ({ article, baseUrl }: BlogProps): JSX.Element => {
  const nextArticle = article.attributes.nextArticle;

  return (
    <>
      <div className="bg-white">
        <div className="relative text-left xl:text-center md:mb-8 mt-8 h-auto">
          <div className="absolute hidden transform rotate-180 -left-10 -top-5 w-96 h-36 xl:inline-block">
            <Image
              src="/shared/shapes.svg"
              alt="shapes doodles"
              objectFit="contain"
              layout="fill"
              priority
            />
          </div>

          <h1
            style={{ lineHeight: "normal" }}
            className="max-w-[845px] text-center mx-auto mb-6 lg:mb-8 font-bold text-[44px] md:text-[56px] xl:text-[76px] xs:leading-[38px] md:leading-[48px] xl:leading-[80px]"
          >
            {article.attributes.title}
          </h1>

          {article?.attributes?.cover?.data?.attributes?.formats?.large?.url && (
            <div className="relative w-full h-[250px] xs:h-[250px] sm:h-[350px] md:h-[450px] lg:h-[550px] xl:h-[600px] mx-auto mb-6 md:mb-8 max-w-[1200px] rounded-lg">
              <Image
                src={article.attributes.cover.data.attributes.formats.large.url}
                alt={article.attributes.title}
                layout="fill"
                style={{
                  width: "100%",
                  height: "auto",
                }}
                priority
                className="rounded-[15px] border object-contain xs:object-cover"
              />
            </div>
          )}

          <h2 className=" font-semibold text-[32px] leading-[40px] max-w-[845px] text-center lg:text-left mx-auto">
            {article.attributes.subTitle}
          </h2>

          <div className="border-t border-b border-grey-200 w-full mx-auto mt-6 max-w-[845px]">
            <div className="md:px-6 px-0 py-4">
              <div
                className={`flex relative flex-col items-left md:flex-row md:items-center justify-center md:justify-between align-middle w-full`}
              >
                <div
                  className={
                    "w-full md:w-auto hidden md:flex flex-row justify-between align-middle items-center md:mb-0"
                  }
                >
                  <div
                    className={`relative min-w-[63px] min-h-[63px] w-[63px] h-[63px] mr-2 md:mr-4 items-center align-middle`}
                  >
                    <Image
                      src={
                        article.attributes.author.data?.attributes.avatar.data.attributes.formats
                          ?.thumbnail?.url || "/icons/avatar.svg"
                      }
                      alt={article.attributes.title || "Author"}
                      width={"100%"}
                      height={"100%"}
                      layout="responsive"
                      objectFit="cover"
                      priority
                      className="rounded-full"
                    />
                  </div>

                  <div className="w-auto  xl:mr-14 font-semibold">
                    {article.attributes.author.data?.attributes.name}{" "}
                  </div>
                </div>

                {/* Published */}
                <div className={"w-auto md:flex flex-col hidden text-left"}>
                  <div className="font-semibold text-landing-blue/60 w-max md:w-auto">
                    Published on
                  </div>
                  <div className="font-semibold w-max md:w-auto">
                    {formatISODateToReadableDate(article.attributes.addedOn)}
                  </div>
                </div>
                {/* Published */}

                <div className="font-semibold text-landing-blue/60 hidden lg:flex text-right w-max">
                  {article.attributes.readingTime} min read
                </div>
                <div className="h-[30px] w-[30px] max-h-[30px] max-w-[30px] relative hidden md:block">
                  <ShareButton article={article} />
                </div>
                <Link href={baseUrl}>
                  <a className="hidden md:flex flex-row align-middle items-center text-landing-orange font-bold">
                    <RightArrow className="md:mr-3  rotate-180" />
                    Back to all articles
                  </a>
                </Link>

                {/* Mobile */}
                <div className="flex md:hidden flex-row justify-between items-center mb-4">
                  <div
                    className={`md:w-auto flex flex-row justify-between align-middle items-center`}
                  >
                    <div
                      className={`relative min-w-[63px] min-h-[63px] w-[63px] h-[63px] mr-2 md:mr-4 items-center align-middle`}
                    >
                      <Image
                        src={
                          article.attributes.author.data?.attributes.avatar.data.attributes.formats
                            ?.thumbnail?.url || "/icons/avatar.svg"
                        }
                        alt={article.attributes.title || "Author"}
                        width={"100%"}
                        height={"100%"}
                        layout="responsive"
                        objectFit="cover"
                        priority
                        className="rounded-full"
                      />
                    </div>

                    <div className="w-auto  xl:mr-14 font-semibold">
                      {article.attributes.author.data?.attributes.name}{" "}
                    </div>
                  </div>

                  {/* Published */}
                  <div className="w-auto flex flex-col text-left">
                    <div className="font-semibold text-landing-blue/60 w-max md:w-auto">
                      Published on
                    </div>
                    <div className="font-semibold w-max md:w-auto">
                      {formatISODateToReadableDate(article.attributes.addedOn)}
                    </div>
                  </div>
                  {/* Published */}
                </div>

                <div className="flex flex-row justify-between md:hidden w-full">
                  <div className="font-semibold text-landing-blue/60 text-right w-max">
                    {article.attributes.readingTime} min read
                  </div>
                  <div className="h-[30px] w-[30px] relative">
                    <ShareButton article={article} />
                  </div>
                  <Link href={baseUrl}>
                    <a className="flex flex-row align-middle items-center text-landing-orange font-bold">
                      <RightArrow className="mr-3  rotate-180" />
                      Back
                    </a>
                  </Link>
                </div>
                {/* Mobile */}
              </div>
            </div>
          </div>
        </div>

        <div className="absolute hidden -right-10 -top-5 w-96 h-36 xl:inline-block">
          <Image
            src="/shared/shapes.svg"
            alt="shapes doodles 2"
            objectFit="contain"
            layout="fill"
            priority
          />
        </div>
      </div>

      <div className="bg-white">
        <section className="flex flex-col w-full mx-auto mt-9 max-w-[845px] md:mb-12">
          <ReactMarkdown
            className={"markdownContent"}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            linkTarget="_blank"
          >
            {article.attributes.content}
          </ReactMarkdown>

          <div className="flex flex-row flex-wrap mt-10 mb-2">
            <span className="flex flex-row justify-between">
              <div className="h-[30px] w-[30px] flex relative mt-2">
                <ShareButton article={article} />
              </div>
            </span>
            {article.attributes.categories.data.map((cat) => (
              <>
                <div
                  key={cat.attributes.slug}
                  className="rounded-full flex mx-2 mb-4 px-4 py-0.5 text-grey-400 text-2xl bg-landing-gray"
                >
                  {cat.attributes.name}
                </div>
              </>
            ))}
          </div>

          {nextArticle.data && (
            <div>
              <div className="font-serif text-[32px] leading-10 mb-5 mt-3.5 line-clamp-2 ">
                Next blog post
              </div>
              <NextBlogPost baseUrl={baseUrl} nextArticle={nextArticle as any} />
            </div>
          )}
        </section>
        <div className="left-0 mt-4 right-0 overflow-hidden bg-repeat-x h-24 md:h-32 xl:h-48 bg-[url('/intermediaries/city-outline-mobile.svg')]  md:bg-[url('/intermediaries/city-outline-tablet.svg')] xl:bg-[url('/intermediaries/city-outline-desktop.svg')]" />
      </div>
    </>
  );
};
